import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ClockIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarDaysIcon,
  UsersIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Plus, MoreVertical, Copy, Trash2, Edit, Users, FileText, RefreshCw } from 'lucide-react';
import { useToast } from '../../components/ui/use-toast';

const statusOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 'planned', label: 'Planlanan' },
  { value: 'active', label: 'Aktif' },
  { value: 'completed', label: 'Tamamlanan' },
  { value: 'cancelled', label: 'İptal Edilen' },
];

const OrganizationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [orgTypes, setOrgTypes] = useState([]);
  const [allInstitutions, setAllInstitutions] = useState([]);
  // Local input states (not synced to URL on every keystroke)
  const [provinceInput, setProvinceInput] = useState(searchParams.get('province') || '');

  // Auto refresh states
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Dropdown dışında tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setActiveDropdown(null);
      }
    };
    
    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  // Auto refresh effect - 3 saniyede bir yenile
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        queryClient.invalidateQueries(['organizations']);
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, queryClient]);

  // Manuel yenileme fonksiyonu
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries(['organizations']);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Get current filters from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentStatus = searchParams.get('status') || 'all';
  const currentSearch = searchParams.get('search') || '';
  const currentOrgType = searchParams.get('organization_type') || '';
  const currentProvince = searchParams.get('province') || '';
  const currentInstitution = searchParams.get('institution') || '';
  const currentDateFrom = searchParams.get('date_from') || '';
  const currentDateTo = searchParams.get('date_to') || '';

  // Load organization types and institutions for filter dropdowns
  useEffect(() => {
    api.get('/organizations/lookup/types').then(res => {
      setOrgTypes(res.data?.data || []);
    }).catch(() => {});
    api.get('/institutions', { params: { limit: 200 } }).then(res => {
      const list = res.data?.data || res.data || [];
      setAllInstitutions(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  // Active filter count (excluding page/search)
  const activeFilterCount = [
    currentStatus !== 'all' ? currentStatus : '',
    currentOrgType,
    currentProvince,
    currentInstitution,
    currentDateFrom,
    currentDateTo,
  ].filter(Boolean).length;

  // Build query parameters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.set('page', currentPage.toString());
    params.set('limit', '10');

    if (currentStatus !== 'all') params.set('status', currentStatus);
    if (currentSearch) params.set('search', currentSearch);
    if (currentOrgType) params.set('organization_type', currentOrgType);
    if (currentProvince) params.set('province', currentProvince);
    if (currentInstitution) params.set('institution', currentInstitution);
    if (currentDateFrom) params.set('date_from', currentDateFrom);
    if (currentDateTo) params.set('date_to', currentDateTo);

    return params.toString();
  };

  // Fetch organizations
  const { data: organizationsData, isLoading, error } = useQuery({
    queryKey: ['organizations', currentPage, currentStatus, currentSearch, currentOrgType, currentProvince, currentInstitution, currentDateFrom, currentDateTo],
    queryFn: () => api.get(`/organizations?${buildQueryParams()}`).then(res => res.data),
  });

  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset to page 1 when filtering
    if (updates.page === undefined) {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams({ search: searchTerm });
  };

  const handleFilterChange = (filterType, value) => {
    updateSearchParams({ [filterType]: value });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setProvinceInput('');
    setSearchParams(new URLSearchParams({ page: '1' }));
  };

  // Copy organization mutation
  const copyOrganizationMutation = useMutation({
    mutationFn: (organizationId) => api.post(`/organizations/${organizationId}/copy`),
    onSuccess: (data) => {
      toast({
        title: "Başarılı",
        description: "Organizasyon kopyalandı",
      });
      queryClient.invalidateQueries(['organizations']);
      navigate(`/organizations/${data.data.organization.id}`);
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Organizasyon kopyalanamadı",
        variant: "destructive",
      });
    },
  });

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ organizationId, status, reason }) =>
      api.put(`/organizations/${organizationId}/status`, { status, cancellation_reason: reason }),
    onSuccess: (_, { status }) => {
      const labels = { planned: 'Planlanan', active: 'Aktif', completed: 'Tamamlandı', cancelled: 'İptal Edildi' };
      toast({ title: 'Başarılı', description: `Durum "${labels[status]}" olarak güncellendi` });
      queryClient.invalidateQueries(['organizations']);
    },
    onError: (error) => {
      toast({ title: 'Hata', description: error.response?.data?.message || 'Durum güncellenemedi', variant: 'destructive' });
    },
  });

  const handleCopyOrganization = (organizationId) => {
    if (window.confirm('Bu organizasyonu kopyalamak istediğinizden emin misiniz?')) {
      copyOrganizationMutation.mutate(organizationId);
    }
  };

  const handleStatusChange = (organizationId, newStatus, currentStatus) => {
    if (newStatus === currentStatus) return;
    const labels = { planned: 'Planlanan', active: 'Aktif', completed: 'Tamamlandı', cancelled: 'İptal Edildi' };
    if (newStatus === 'cancelled') {
      const reason = window.prompt(`"${labels[newStatus]}" durumuna almak istediğinizden emin misiniz?\nİptal nedeni (opsiyonel):`);
      if (reason === null) return;
      updateStatusMutation.mutate({ organizationId, status: newStatus, reason });
    } else {
      if (window.confirm(`Durumu "${labels[newStatus]}" olarak güncellemek istiyor musunuz?`)) {
        updateStatusMutation.mutate({ organizationId, status: newStatus });
      }
    }
  };

  // Delete mutation
  const deleteOrganizationMutation = useMutation({
    mutationFn: async (organizationId) => {
      const response = await api.delete(`/organizations/${organizationId}`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['organizations']);
      toast({
        title: "Başarılı",
        description: data.message || "Organizasyon silindi",
        duration: 3000
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.response?.data?.message || "Organizasyon silinirken hata oluştu",
        duration: 5000
      });
    }
  });

  const handleDeleteOrganization = (organizationId, organizationName) => {
    if (window.confirm(`"${organizationName}" organizasyonunu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      deleteOrganizationMutation.mutate(organizationId);
    }
  };

  // Sadece super_admin ve admin silebilir
  const canDeleteOrganization = ['super_admin', 'admin'].includes(user?.role);

  const getStatusBadge = (status) => {
    const statusConfig = {
      planned: { label: 'Planlanan', className: 'bg-blue-100 text-blue-800' },
      active: { label: 'Aktif', className: 'bg-green-100 text-green-800' },
      completed: { label: 'Tamamlanan', className: 'bg-gray-100 text-gray-800' },
      cancelled: { label: 'İptal', className: 'bg-red-100 text-red-800' },
    };
    
    const config = statusConfig[status] || statusConfig.planned;
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.slice(0, 5); // HH:MM format
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive">
              <p>Organizasyonlar yüklenirken hata oluştu</p>
              <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const organizations = organizationsData?.data || [];
  const pagination = organizationsData?.pagination || {};

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Auto Refresh Controls - Sağ üst köşe */}
      <div className="flex justify-end mb-2">
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 shadow-sm">
          {/* Otomatik Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-800"
            />
            <span className="text-sm text-slate-200 select-none">Otomatik</span>
          </label>
          {/* Manuel Yenile Butonu */}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="p-1.5 rounded-md hover:bg-slate-700 transition-colors disabled:opacity-50"
            title="Yenile"
          >
            <RefreshCw className={`h-4 w-4 text-slate-300 ${isRefreshing || (autoRefresh && isLoading) ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organizasyonlar</h1>
          <p className="text-muted-foreground">
            Etkinlik organizasyonlarını görüntüleyin ve yönetin
          </p>
        </div>
        <Button onClick={() => navigate('/organizations/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni Organizasyon
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <form onSubmit={handleSearch} className="flex space-x-2">
                  <Input
                    placeholder="Organizasyon adı, numarası ile arama..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" variant="outline">
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </Button>
                </form>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-2"
                >
                  <FunnelIcon className="h-4 w-4" />
                  <span>Filtreler</span>
                  {activeFilterCount > 0 && (
                    <span className="ml-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
                {(activeFilterCount > 0 || currentSearch) && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-gray-500 hover:text-red-600">
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Temizle
                  </Button>
                )}
              </div>
            </div>

            {/* Active filter chips */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2">
                {currentStatus !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                    Durum: {statusOptions.find(s => s.value === currentStatus)?.label}
                    <button onClick={() => handleFilterChange('status', 'all')} className="hover:text-blue-900"><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                )}
                {currentOrgType && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                    Tür: {currentOrgType}
                    <button onClick={() => handleFilterChange('organization_type', '')} className="hover:text-purple-900"><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                )}
                {currentProvince && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                    İl: {currentProvince}
                    <button onClick={() => handleFilterChange('province', '')} className="hover:text-green-900"><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                )}
                {currentInstitution && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                    Kurum: {currentInstitution}
                    <button onClick={() => handleFilterChange('institution', '')} className="hover:text-orange-900"><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                )}
                {currentDateFrom && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                    Başlangıç ≥ {currentDateFrom}
                    <button onClick={() => handleFilterChange('date_from', '')} className="hover:text-gray-900"><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                )}
                {currentDateTo && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                    Başlangıç ≤ {currentDateTo}
                    <button onClick={() => handleFilterChange('date_to', '')} className="hover:text-gray-900"><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                )}
              </div>
            )}

            {/* Expanded Filters Panel */}
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t">
                {/* Durum */}
                <div>
                  <label className="block text-sm font-medium mb-1.5">Durum</label>
                  <select
                    value={currentStatus}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Organizasyon Türü */}
                <div>
                  <label className="block text-sm font-medium mb-1.5">Organizasyon Türü</label>
                  <select
                    value={currentOrgType}
                    onChange={(e) => handleFilterChange('organization_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tümü</option>
                    {orgTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* İl / Şehir */}
                <div>
                  <label className="block text-sm font-medium mb-1.5">İl / Şehir</label>
                  <Input
                    placeholder="İstanbul, Ankara..."
                    value={provinceInput}
                    onChange={(e) => setProvinceInput(e.target.value)}
                    onBlur={(e) => handleFilterChange('province', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleFilterChange('province', e.target.value); }}
                  />
                </div>

                {/* Kurum — select dropdown */}
                <div>
                  <label className="block text-sm font-medium mb-1.5">Kurum</label>
                  <select
                    value={currentInstitution}
                    onChange={(e) => handleFilterChange('institution', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tümü</option>
                    {allInstitutions.map((inst) => (
                      <option key={inst.id} value={inst.name}>{inst.name}</option>
                    ))}
                  </select>
                </div>

                {/* Başlangıç Tarihi - Başlangıç */}
                <div>
                  <label className="block text-sm font-medium mb-1.5">Başlangıç Tarihi (Başlangıç)</label>
                  <Input
                    type="date"
                    value={currentDateFrom}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  />
                </div>

                {/* Başlangıç Tarihi - Bitiş */}
                <div>
                  <label className="block text-sm font-medium mb-1.5">Başlangıç Tarihi (Bitiş)</label>
                  <Input
                    type="date"
                    value={currentDateTo}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Organizations List */}
      <div className="grid gap-6">
        {organizations.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <BuildingOfficeIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Organizasyon bulunamadı</h3>
                <p className="text-muted-foreground mb-4">
                  {currentSearch || currentStatus !== 'all' 
                    ? 'Arama kriterlerinize uygun organizasyon bulunamadı.'
                    : 'Henüz hiç organizasyon oluşturulmamış.'
                  }
                </p>
                {(!currentSearch && currentStatus === 'all') && (
                  <Button onClick={() => navigate('/organizations/new')}>
                    <Plus className="h-4 w-4 mr-2" />
                    İlk Organizasyonu Oluştur
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          organizations.map((organization) => (
            <Card key={organization.id} className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-6" onClick={() => navigate(`/organizations/${organization.id}`)}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold">
                        {organization.organization_name}
                      </h3>
                      {getStatusBadge(organization.status)}
                      {organization.is_cancelled && (
                        <Badge className="bg-red-100 text-red-800">İptal</Badge>
                      )}
                      {organization.is_multiple_organization && (
                        <Badge className="bg-purple-100 text-purple-800">Çoklu</Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Numara:</span>
                        <span className="font-medium">{organization.organization_number}</span>
                      </div>

                      {organization.start_date && (
                        <div className="flex items-center space-x-2">
                          <CalendarDaysIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Başlangıç:</span>
                          <span>{formatDate(organization.start_date)} {formatTime(organization.start_time)}</span>
                        </div>
                      )}

                      {organization.authorized_person && (
                        <div className="flex items-center space-x-2">
                          <UsersIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Yetkili:</span>
                          <span>{organization.authorized_person}</span>
                        </div>
                      )}

                      {organization.ambulance_count > 0 && (
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Ambulans:</span>
                          <span>{organization.ambulance_count}</span>
                        </div>
                      )}
                    </div>

                    {(organization.address || organization.district || organization.province) && (
                      <div className="flex items-center space-x-2 mt-2 text-sm">
                        <MapPinIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {[organization.address, organization.district, organization.province]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </div>
                    )}

                    {organization.general_description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {organization.general_description}
                      </p>
                    )}

                    {/* Team Assignments Section */}
                    {organization.team_assignments && organization.team_assignments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <UsersIcon className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Atanan Ekipler ({organization.team_assignments.length}/{organization.ambulance_count || 0})
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {organization.team_assignments.map((assignment) => (
                            <Badge
                              key={assignment.id}
                              variant="outline"
                              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                            >
                              <span className="text-xs">
                                {assignment.team?.code || assignment.team?.name || `Ekip #${assignment.team_id}`}
                              </span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {organization.ambulance_count > 0 && (!organization.team_assignments || organization.team_assignments.length === 0) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-2">
                          <UsersIcon className="h-4 w-4 text-orange-500" />
                          <span className="text-sm text-orange-600">
                            {organization.ambulance_count} ambulans için ekip ataması bekleniyor
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs text-muted-foreground">
                      <span>{organization.institution_name}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>{organization.created_by_name}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>{formatDate(organization.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 sm:ml-4 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/organizations/${organization.id}`);
                      }}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>

                    <div className="relative">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(activeDropdown === organization.id ? null : organization.id);
                        }}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>

                      {activeDropdown === organization.id && (
                        <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                          <div className="py-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/organizations/${organization.id}/edit`);
                                setActiveDropdown(null);
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Düzenle
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyOrganization(organization.id);
                                setActiveDropdown(null);
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Kopyala
                            </button>
                              {/* Durum Güncelleme */}
                            <div className="border-t border-gray-100 pt-1 pb-1">
                              <p className="px-4 py-1 text-xs text-gray-400 font-medium uppercase tracking-wide">Durum Değiştir</p>
                              {[
                                { value: 'planned', label: 'Planlanan', color: 'text-blue-600 hover:bg-blue-50' },
                                { value: 'active', label: 'Aktif', color: 'text-green-600 hover:bg-green-50' },
                                { value: 'completed', label: 'Tamamlandı', color: 'text-gray-600 hover:bg-gray-50' },
                                { value: 'cancelled', label: 'İptal Et', color: 'text-red-600 hover:bg-red-50' },
                              ].filter(opt => opt.value !== organization.status).map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(organization.id, opt.value, organization.status);
                                    setActiveDropdown(null);
                                  }}
                                  className={`flex items-center w-full px-4 py-2 text-sm ${opt.color}`}
                                >
                                  {opt.value === 'planned' && '📅 '}
                                  {opt.value === 'active' && '▶️ '}
                                  {opt.value === 'completed' && '✅ '}
                                  {opt.value === 'cancelled' && '❌ '}
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            {canDeleteOrganization && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteOrganization(organization.id, organization.organization_name);
                                  setActiveDropdown(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Sil
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-8">
          <div className="text-sm text-muted-foreground">
            {pagination.total} organizasyondan {((pagination.page - 1) * pagination.limit) + 1}-
            {Math.min(pagination.page * pagination.limit, pagination.total)} arası gösteriliyor
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => updateSearchParams({ page: pagination.page - 1 })}
            >
              Önceki
            </Button>
            
            <span className="text-sm">
              Sayfa {pagination.page} / {pagination.pages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => updateSearchParams({ page: pagination.page + 1 })}
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsPage; 